import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
    adjustFromTimeZoneSafely,
    adjustToTimeZone,
    adjustToTimeZoneSafely,
    DispatchRequest,
    DispatchRequestEntity,
    dispatchRequestStatus,
    getObjectId,
    getRequiredObjectId,
    Quantity,
    transformDispatchRequest,
    useCreateDispatchRequestMutation,
    useGetDispatchRequestQuery,
    useUpdateDispatchRequestMutation
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {DispatchRequestForm, DispatchRequestFormValues, ThemedPage, useAuth, useRegion} from "@ots-frontend/ots-ui";
import {notifications} from "@mantine/notifications";
import {v4 as uuid} from "uuid";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppCategory} from "../../store/ui/selectors";

type Props = {
    id?: string;
    copy?: boolean;
    formDefaults?: Partial<DispatchRequestFormValues>
}

export const DispatchRequestFormPageRoute = () => {
    const {state} = useLocation();
    const id = state?.id;
    const copy = state?.copy;
    const formDefaults = state?.formDefaults;
    return <DispatchRequestRequestFormPage id={id} copy={copy} formDefaults={formDefaults}/>;
};


export const toFormDefaultsFromDispatchRequest = (request: DispatchRequestEntity | undefined): Partial<DispatchRequestFormValues> => {
    let quantity: Quantity | undefined = undefined;

    if (request && request.quantity && request.quantityUom) {
        quantity = {
            number: request.quantity,
            units: request.quantityUom
        };
    } else {
        quantity = {
            number: 1,
            units: "loads",
        }
    }

    return {
        scheduledDate: adjustFromTimeZoneSafely(request?.scheduledDttm),
        scheduledEndDate: adjustFromTimeZoneSafely(request?.scheduledEndDttm),
        acceptTimeLimit: request?.acceptTimeLimit,
        truckCompany: request?.truckingCompanyId,
        quantity: quantity,
        freightType: request?.freightTypeId,
        serviceType: request?.serviceTypeId,
        pickupSite: request?.pickupSiteId,
        pickupWell: request?.pickupWellId,
        pickupContainer: request?.pickupContainerId,
        deliverySite: request?.deliverySiteId,
        deliveryWell: request?.deliveryWellId,
        deliveryContainer: request?.deliveryContainerId,
        externalId: request?.externalId,
        authorizedBy: request?.authorizeId,
        workOrderNumber: request?.workOrderNumber,
        category: request?.category,
    };
};

export const DispatchRequestRequestFormPage = (props: Props) => {
    const {id, copy = false, formDefaults} = props;

    const navigate = useNavigate();
    const auth = useAuth();
    const {regionId} = useRegion()
    const {t} = useTranslation(["translation"]);

    const appCategory = useAppSelector(getAppCategory)

    const {currDispatchRequest, isLoading} = useGetDispatchRequestQuery(id ?? skipToken, {
        selectFromResult: ({data, isLoading}) => ({
            currDispatchRequest: data ? transformDispatchRequest(data) : undefined,
            isLoading: isLoading
        })
    });

    const _formDefaults: Partial<DispatchRequestFormValues> = useMemo(() => {
        return formDefaults ? formDefaults : toFormDefaultsFromDispatchRequest(currDispatchRequest);
    }, [currDispatchRequest, formDefaults]);

    const titleKey: string = useMemo(() => {
        if (currDispatchRequest && !copy) {
            return "page.dispatchrequest.form.title.edit";
        } else {
            return "page.dispatchrequest.form.title.new";
        }
    }, [currDispatchRequest, copy]);

    const [createDispatchRequest, {isLoading: isCreating}] = useCreateDispatchRequestMutation();
    const [updateDispatchRequest, {isLoading: isUpdating}] = useUpdateDispatchRequestMutation();

    const onDispatchRequestSubmit = useCallback((formValues: DispatchRequestFormValues) => {
        if (currDispatchRequest && !copy) {
            const request: Partial<DispatchRequest> = {
                ...currDispatchRequest.asTypeValue(),
                authentication: auth.userId,
                scheduledDttm: adjustToTimeZone(formValues.scheduledDate).getTime(),
                scheduledEndDttm: adjustToTimeZoneSafely(formValues.scheduledEndDate)?.getTime(),
                truckingCompanyId: getRequiredObjectId(formValues.truckCompany),
                quantityDecimal: formValues.quantity.number,
                quantityUnitOfMeasure: formValues.quantity.units,
                freightTypeId: getObjectId(formValues.freightType),
                serviceTypeId: getObjectId(formValues.serviceType),
                pickupSiteId: getObjectId(formValues.pickupSite),
                pickupWellId: getObjectId(formValues.pickupWell),
                pickupContainerId: getObjectId(formValues.pickupContainer),
                deliverySiteId: getObjectId(formValues.deliverySite),
                deliveryWellId: getObjectId(formValues.deliveryWell),
                deliveryContainerId: getObjectId(formValues.deliveryContainer),
                comment: formValues.comments,
                workOrderNumber: formValues.workOrderNumber,
                externalId: formValues.externalId,
                acceptTimeLimit: formValues.acceptTimeLimit,
            };
            updateDispatchRequest(request)
                .unwrap()
                .then(() => {
                    notifications.show({
                        color: "green",
                        title: "Dispatch Request Updated",
                        message: "The vendor will be notified of the updated request."
                    });
                    navigate(-1);
                })
                .catch((rejected) =>
                    notifications.show({
                        color: "red",
                        title: "Error",
                        message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
                    })
                );
        } else {
            const authorizedBy = getObjectId(formValues.authorizedBy)

            const request: Partial<DispatchRequest> = {
                authentication: auth.userId,
                id: uuid(),
                scheduledDttm: adjustToTimeZone(formValues.scheduledDate).getTime(),
                scheduledEndDttm: adjustToTimeZoneSafely(formValues.scheduledEndDate)?.getTime(),
                status: dispatchRequestStatus.pending.code,
                operatorId: auth.companyId,
                truckingCompanyId: getRequiredObjectId(formValues.truckCompany),
                regionId: regionId,
                createdById: auth.userId,
                quantityDecimal: formValues.quantity.number,
                quantityUnitOfMeasure: formValues.quantity.units,
                freightTypeId: getObjectId(formValues.freightType),
                serviceTypeId: getObjectId(formValues.serviceType),
                pickupSiteId: getObjectId(formValues.pickupSite),
                pickupWellId: getObjectId(formValues.pickupWell),
                pickupContainerId: getObjectId(formValues.pickupContainer),
                deliverySiteId: getObjectId(formValues.deliverySite),
                deliveryWellId: getObjectId(formValues.deliveryWell),
                deliveryContainerId: getObjectId(formValues.deliveryContainer),
                authorized: authorizedBy ? true : undefined,
                authorizeId: authorizedBy,
                comment: formValues.comments,
                workOrderNumber: formValues.workOrderNumber,
                externalId: formValues.externalId,
                acceptTimeLimit: formValues.acceptTimeLimit,
            };
            createDispatchRequest(request)
                .unwrap()
                .then(() => {
                    notifications.show({
                        color: "green",
                        title: "Dispatch Request Created",
                        message: "The vendor will be notified of the new request."
                    });
                    navigate(-1);
                })
                .catch((rejected) =>
                    notifications.show({
                        color: "red",
                        title: "Error",
                        message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
                    })
                );
        }
    }, [currDispatchRequest, createDispatchRequest, updateDispatchRequest, auth, regionId, navigate, copy]);

    return (
        <ThemedPage>
            {(classes) => (
                <Container className={classes.container} fluid={true} py="md">
                    <Center>
                        {isLoading && <Loader/>}
                        {!isLoading && (
                            <Paper miw={1000} shadow="sm" p="md" py="md">
                                <Group position="apart">
                                    <Title align="center">
                                        {t(titleKey)}
                                    </Title>
                                    <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                                </Group>
                                <Space h="md"/>
                                <DispatchRequestForm
                                    defaultValues={_formDefaults}
                                    options={{
                                        category: appCategory,
                                        categoryDisabled: (!copy && typeof currDispatchRequest !== 'undefined'),
                                        truckCompanySelectionDisabled: (!copy && typeof currDispatchRequest !== 'undefined'),
                                        authorizedByDisabled: (!copy && typeof currDispatchRequest !== 'undefined'),
                                        crudeOilFreightTypeDefaultEnabled: copy || typeof currDispatchRequest === 'undefined',
                                    }}
                                    submitting={isCreating || isUpdating}
                                    onSubmit={onDispatchRequestSubmit}/>
                            </Paper>
                        )}
                    </Center>
                </Container>
            )}
        </ThemedPage>
    );
};

